import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { PackageSearch, X } from "lucide-react";

import { productService, ProductPickSheet, type ProductNode } from "@/entities/product";
import { Button } from "@/shared/ui/button";
import {
  freeCreateProductFromNode,
  pickableProducts,
  type FreeCreateProduct,
} from "../../../model/free-create/productAdapter";

/**
 * 产品选择抽屉 —— 2026-07-28 P2。
 *
 * ═══ 为什么换掉平铺 select ═════════════════════════════════════════════════
 * 原来是一个 `<select>` 把整棵产品树拍平。造了 36 个产品(4 品类 × 9)之后实测,
 * 下拉里是 `经典美式 / 挂耳礼盒 10 包 / 手冲壶 / 可颂 / 生椰拿铁 …` —— **跨品类乱序**,
 * 品类层完全没体现。真实租户上百个产品就没法用了。三个具体缺陷:
 *   ① 没有分组:咖啡和手冲壶混排
 *   ② 没有搜索
 *   ③ **选之前看不到这个产品带什么进来**,选完才显示「3 条卖点 · 3 项参数」——选择是盲的
 *
 * ★2026-07-30(D7):抽屉本体搬去 `entities/product/ui/ProductPickSheet`,长图搭建器
 * 共用同一个 —— 那边此前还是 37 项的裸 select。本文件只剩「自由创作的字段映射 + 外框说明」。
 */
export function ProductPickerDrawer({
  product,
  onPick,
  disabled,
  compact = false,
}: {
  product: FreeCreateProduct | null;
  onPick: (product: FreeCreateProduct | null) => void;
  disabled?: boolean;
  /**
   * 2026-07-30 —— 大框内那一行用:只出触发器,不要外面那张说明卡。
   * ★说明没丢,挪进抽屉里(`footerHint` 已经在说"选了会逐字带进指令");
   *   框里那一行要的是「一个 chip」,一张 60px 高的卡摆在文案框里会把它顶散。
   */
  compact?: boolean;
}) {
  // 拉不到不阻断 —— 手写文案仍然是完整可用的路径(与长图那条同口径)
  const {
    data: nodes = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["tenant-products"],
    queryFn: () => productService.list(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const products = useMemo(() => pickableProducts(nodes), [nodes]);

  const pick = (n: ProductNode | null) => {
    onPick(n ? freeCreateProductFromNode(n, nodes) : null);
  };

  const body = (
    <div className="flex flex-wrap items-center gap-2">
      <ProductPickSheet
        products={products}
        allNodes={nodes}
        pickedCode={product?.code}
        onPick={pick}
        disabled={disabled}
        loading={isLoading}
        error={isError}
        onRetry={() => void refetch()}
        footerHint={
          product
            ? `已选:${product.name} · ${product.sellingPoints.length} 条卖点 · ${product.specs.length} 项参数`
            : undefined
        }
      />

      {product && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          className="h-8 gap-1 text-[13px] text-muted-foreground"
          onClick={() => onPick(null)}
        >
          <X className="h-3 w-3" />
          清除
        </Button>
      )}
    </div>
  );

  if (compact) return body;

  return (
    <div className="rounded-lg border border-border/60 bg-card/30 px-3 py-2.5">
      <div className="mb-1.5 flex items-center gap-1.5 text-[12.5px] font-medium text-muted-foreground">
        <PackageSearch className="h-3.5 w-3.5" />
        从产品库选 · 名称/卖点/参数逐字带进出图指令(也可以不选,自己写)
      </div>
      {body}
      {product && (
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted-foreground">
          已带入:{product.sellingPoints.length} 条卖点 · {product.specs.length} 项参数
          {product.summary ? " · 产品说明" : ""}。<b>逐字取自产品库,不会被改写</b>;
          你写的文案照旧,两段各占各的。
        </p>
      )}
    </div>
  );
}
